import {
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useState,
} from 'react';
import type { UIEvent } from 'react';
import type {
  UseInfiniteScrollParams,
  UseScrollPositionReturn,
  UseScrollToBottomParams,
  UseScrollToBottomReturn,
} from '../../types/scroll/movement.types';
import { useScrollIntent } from './use-scroll-intent';
import type { ScrollDirection } from '../../types/scroll/scroll-intent.types';

/**
 * Hook for handling infinite scroll behavior on a container element.
 *
 * @param params - Configuration for infinite scroll behavior
 * @param params.containerRef - Ref to the scrollable container element
 * @param params.layoutBoundaryRef - Optional surrounding layout for input classification.
 * @param params.isFetching - Whether data is currently being fetched
 * @param params.hasMore - Whether there is more data to load
 * @param params.fetchMore - Callback to fetch more data
 * @param params.direction - Direction to trigger fetch ('up' or 'down'), defaults to 'up'
 * @param params.threshold - Fraction of viewport height near the edge, defaults to 0.3.
 * @param params.requireUserIntent - Require user movement toward the selected edge.
 * @param params.restorePosition - Disable when another hook owns viewport positioning.
 * @param params.resetKey - List identity that resets pagination ownership.
 * @param params.positionRevision - Content revision after external positioning.
 * @param params.onBeforeFetch - Capture position or other local state before fetching.
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * useInfiniteScroll({
 *   containerRef,
 *   isFetching: isLoading,
 *   hasMore: hasNextPage,
 *   fetchMore: loadMore,
 *   direction: 'down',
 *   threshold: 0.2,
 * });
 * ```
 */
export const useInfiniteScroll = ({
  containerRef,
  layoutBoundaryRef,
  isFetching,
  hasMore,
  fetchMore,
  direction = 'up',
  threshold = 0.3,
  requireUserIntent = false,
  restorePosition = true,
  resetKey,
  positionRevision,
  onBeforeFetch,
}: UseInfiniteScrollParams) => {
  const prevScrollHeightRef = useRef<number>(0);
  const pendingRequest = useRef<object | null>(null);

  useLayoutEffect(() => {
    prevScrollHeightRef.current = 0;
    pendingRequest.current = null;
  }, [containerRef, resetKey]);

  const requestPage = useCallback(() => {
    const container = containerRef.current;
    if (!container) return false;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const thresholdDistance = clientHeight * threshold;
    const distanceFromEdge =
      direction === 'up' ? scrollTop : scrollHeight - scrollTop - clientHeight;

    const shouldFetchMore =
      distanceFromEdge <= thresholdDistance &&
      hasMore &&
      !isFetching &&
      !pendingRequest.current;

    if (shouldFetchMore) {
      prevScrollHeightRef.current = scrollHeight;
      onBeforeFetch?.();
      const pending = {};
      pendingRequest.current = pending;
      const finish = () => {
        if (pendingRequest.current === pending) pendingRequest.current = null;
      };
      try {
        void Promise.resolve(fetchMore()).then(finish, finish);
      } catch (error) {
        finish();
        throw error;
      }
      return true;
    }
    return false;
  }, [
    containerRef,
    fetchMore,
    hasMore,
    isFetching,
    direction,
    threshold,
    onBeforeFetch,
  ]);

  const handleIntent = useCallback(
    (movement: ScrollDirection) => movement === direction && requestPage(),
    [direction, requestPage]
  );
  useScrollIntent({
    containerRef,
    layoutBoundaryRef,
    enabled: requireUserIntent,
    resetKey,
    positionRevision,
    onIntent: handleIntent,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || requireUserIntent) return;

    container.addEventListener('scroll', requestPage);
    return () => {
      container.removeEventListener('scroll', requestPage);
    };
  }, [containerRef, requestPage, requireUserIntent]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || isFetching || !restorePosition) return;

    const prevScrollHeight = prevScrollHeightRef.current;
    if (!prevScrollHeight) return;

    const newScrollHeight = container.scrollHeight;

    if (direction === 'up') {
      container.scrollTop = newScrollHeight - prevScrollHeight;
    }

    prevScrollHeightRef.current = 0;
  }, [containerRef, isFetching, direction, restorePosition]);
};

/**
 * Hook for tracking scroll position in a container element.
 * Detects when the user has scrolled near the bottom of the container.
 *
 * @param dependencies - Array of dependencies that reset scroll position tracking
 * @returns Object containing scroll state and handler
 * @returns isAtBottom - Whether the container is scrolled near the bottom
 * @returns handleScroll - Event handler to attach to a scrollable element
 *
 * @example
 * ```tsx
 * const { isAtBottom, handleScroll } = useScrollPosition([messages.length]);
 * return <div onScroll={handleScroll}>...</div>;
 * ```
 */
export function useScrollPosition(
  dependencies: unknown[]
): UseScrollPositionReturn {
  const [isAtBottom, setIsAtBottom] = useState(true);
  const dependenciesKey = JSON.stringify(dependencies);

  useEffect(() => {
    setIsAtBottom(true);
  }, [dependenciesKey]);

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 50;
    setIsAtBottom(atBottom);
  }, []);

  return { isAtBottom, handleScroll };
}

/**
 * Hook for automatically scrolling to the bottom of a container.
 * Scrolls smoothly when new content is added if the user is already at the bottom,
 * or immediately on initial mount.
 *
 * @param params - Configuration for scroll behavior
 * @param params.bottomAnchorRef - Ref to element at the bottom to scroll into view
 * @param params.isAtBottom - Whether container is at bottom (from useScrollPosition)
 * @param params.dependencies - Array of dependencies triggering scroll updates
 * @param params.resetKey - Array of dependencies that reset initial mount behavior
 * @returns Object containing scrollToBottom method for manual scrolling
 *
 * @example
 * ```tsx
 * const bottomRef = useRef<HTMLDivElement>(null);
 * const { isAtBottom, handleScroll } = useScrollPosition([messages.length]);
 * const { scrollToBottom } = useScrollToBottom({
 *   bottomAnchorRef: bottomRef,
 *   isAtBottom,
 *   dependencies: [messages],
 * });
 * ```
 */
export function useScrollToBottom({
  bottomAnchorRef,
  isAtBottom,
  dependencies,
  resetKey = [],
}: UseScrollToBottomParams): UseScrollToBottomReturn {
  const isInitialMount = useRef(true);
  const dependenciesKey = JSON.stringify(dependencies);
  const resetKeyValue = JSON.stringify(resetKey);

  useEffect(() => {
    isInitialMount.current = true;
  }, [resetKeyValue]);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      if (!bottomAnchorRef.current) return;

      const validBehavior: ScrollBehavior =
        behavior === 'auto' || behavior === 'smooth' ? behavior : 'smooth';

      bottomAnchorRef.current.scrollIntoView({ behavior: validBehavior });
    },
    [bottomAnchorRef]
  );

  useEffect(() => {
    if (!isAtBottom || dependencies.length === 0) return;

    if (isInitialMount.current) {
      scrollToBottom('auto');
      isInitialMount.current = false;
    } else {
      scrollToBottom();
    }
  }, [dependencies.length, dependenciesKey, isAtBottom, scrollToBottom]);

  return { scrollToBottom };
}
